import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Grid, InputAdornment, MenuItem, Skeleton, Snackbar, Stack, Switch, TextField, Typography,
} from '@mui/material';
import {
  AutoGraphOutlined, CheckCircleOutline, ContentCopy, KeyOutlined, ScienceOutlined, ShieldOutlined, WarningAmberOutlined,
} from '@mui/icons-material';
import { PageHeader, Panel, inr } from '../components/ui';
import { errorMessage, kiteApi, tradingApi } from '../services/api';
import { KiteCredentials, Mode, TradingConfig } from '../types';
import { t } from '../utils/theme';

const SYMBOL_RE = /^(NSE|BSE):[A-Z0-9&-]+$/;

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Box sx={{ p: 0.9, borderRadius: 2, bgcolor: 'rgba(99,102,241,0.14)', color: t.accentSoft, display: 'flex' }}>{icon}</Box>
    <Box>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" sx={{ color: t.text2 }}>{subtitle}</Typography>
    </Box>
  </Stack>
);

const ModeCard: React.FC<{ selected: boolean; live?: boolean; title: string; text: string; onClick: () => void }> =
  ({ selected, live, title, text, onClick }) => (
    <Box component="button" type="button" onClick={onClick} aria-pressed={selected}
         sx={{
           textAlign: 'left', width: '100%', p: 2, borderRadius: 2, cursor: 'pointer', color: t.text, font: 'inherit',
           bgcolor: selected ? (live ? 'rgba(220,38,38,0.12)' : 'rgba(99,102,241,0.12)') : t.panel2,
           border: `1.5px solid ${selected ? (live ? t.danger : t.accent) : t.border}`, transition: 'border-color 150ms, background-color 150ms',
           '&:hover': { borderColor: live ? t.danger : t.accent }, '&:focus-visible': { outline: `2px solid ${t.accentSoft}`, outlineOffset: 2 },
         }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {live ? <WarningAmberOutlined sx={{ color: t.loss }} /> : <ScienceOutlined sx={{ color: t.accentSoft }} />}
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        <Box sx={{ flex: 1 }} />
        {selected && <CheckCircleOutline sx={{ color: live ? t.loss : t.accentSoft }} fontSize="small" />}
      </Stack>
      <Typography variant="body2" sx={{ color: t.text2, mt: 0.75 }}>{text}</Typography>
    </Box>
  );

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; title: string; text: string; label: string }> =
  ({ checked, onChange, title, text, label }) => (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.25, borderRadius: 2, bgcolor: t.panel2, border: `1px solid ${t.border}`, height: '100%' }}>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} inputProps={{ 'aria-label': label }} />
      <Box>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: t.text2 }}>{text}</Typography>
      </Box>
    </Stack>
  );

const Settings: React.FC = () => {
  const [cfg, setCfg] = useState<TradingConfig | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [liveDialog, setLiveDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [kite, setKite] = useState<KiteCredentials | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [kiteErrors, setKiteErrors] = useState<Record<string, string>>({});
  const [kiteSaving, setKiteSaving] = useState(false);

  useEffect(() => {
    kiteApi.credentials().then((k) => { setKite(k); setApiKey(k.api_key); }).catch(() => undefined);
  }, []);

  const load = () => tradingApi.config().then((c) => { setCfg(c); setDirty(false); })
    .catch((e) => setSnack({ msg: errorMessage(e), severity: 'error' }));
  useEffect(() => { load(); }, []);

  const saveKite = async () => {
    setKiteErrors({});
    setKiteSaving(true);
    try {
      const k = await kiteApi.saveCredentials(apiKey.trim(), apiSecret.trim());
      setKite(k);
      setApiSecret('');
      setSnack({ msg: 'Kite credentials saved. Press "Login to Kite" on the dashboard when a rebalance is due.', severity: 'success' });
    } catch (err: any) {
      if (err?.response?.status === 400 && typeof err.response.data === 'object') setKiteErrors(err.response.data);
      setSnack({ msg: errorMessage(err, 'Could not save Kite credentials'), severity: 'error' });
    } finally {
      setKiteSaving(false);
    }
  };

  const set = (key: keyof TradingConfig, value: any) => {
    setCfg((c) => (c ? { ...c, [key]: value } : c));
    setDirty(true);
  };
  const field = (key: keyof TradingConfig, label: string, extra: any = {}) => (
    <TextField fullWidth size="small" label={label} value={cfg?.[key] ?? ''} error={!!errors[key]}
               helperText={errors[key] || extra.helperText} onChange={(e) => set(key, e.target.value)} {...extra} />
  );
  const rupee = { InputProps: { startAdornment: <InputAdornment position="start">₹</InputAdornment> }, type: 'number' };

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    setErrors({});
    try {
      setCfg(await tradingApi.saveConfig(cfg));
      setDirty(false);
      setSnack({ msg: 'Settings saved', severity: 'success' });
    } catch (err: any) {
      const data = err?.response?.data;
      if (err?.response?.status === 400 && data && typeof data === 'object') {
        const fieldErrors: Record<string, string> = {};
        Object.entries(data).forEach(([k, v]) => { fieldErrors[k] = Array.isArray(v) ? v.join(', ') : String(v); });
        setErrors(fieldErrors);
        setSnack({ msg: fieldErrors.non_field_errors || 'Fix the highlighted fields', severity: 'error' });
      } else {
        setSnack({ msg: errorMessage(err, 'Save failed'), severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const switchMode = async (mode: Mode) => {
    try {
      await tradingApi.setMode(mode);
      setSnack({ msg: `Mode set to ${mode}`, severity: 'success' });
      load();
    } catch (err: any) {
      setSnack({ msg: err?.response?.status === 409 ? 'Sell all holdings first (Emergency stop)' : errorMessage(err), severity: 'error' });
    }
  };

  if (!cfg) return <Stack spacing={2}>{[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={180} />)}</Stack>;

  const perStock = Number(cfg.capital) * (1 - Number(cfg.cash_reserve_pct) / 100) / Math.max(1, cfg.top_n);

  return (
    <Box sx={{ pb: dirty ? 10 : 0 }}>
      <PageHeader title="Settings" subtitle="Broker connection, trading mode and the monthly momentum rules." />

      <Stack spacing={2.5}>
        <Panel>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2.5 }}>
            <SectionTitle icon={<KeyOutlined />} title="Zerodha Kite API" subtitle="From your app at developers.kite.trade" />
            <Chip size="small" icon={kite?.configured ? <CheckCircleOutline /> : undefined}
                  label={kite?.configured ? 'Connected' : 'Not set'} color={kite?.configured ? 'success' : 'warning'} variant="outlined" />
          </Stack>
          <Box sx={{ p: 1.5, mb: 2.5, borderRadius: 2, bgcolor: t.panel2, border: `1px dashed ${t.border}` }}>
            <Typography variant="caption" sx={{ color: t.text3, display: 'block', mb: 0.5 }}>Redirect URL: paste this into your Kite app settings</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontFamily: t.mono, fontSize: '0.85rem', wordBreak: 'break-all', flex: 1 }}>{kite?.redirect_url ?? '…'}</Typography>
              <Button size="small" startIcon={<ContentCopy fontSize="small" />} disabled={!kite}
                      onClick={() => { navigator.clipboard?.writeText(kite?.redirect_url ?? ''); setSnack({ msg: 'Copied', severity: 'success' }); }}>
                Copy
              </Button>
            </Stack>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" label="API key" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                         error={!!kiteErrors.api_key} helperText={kiteErrors.api_key} autoComplete="off" />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" type="password" label="API secret" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
                         autoComplete="new-password" placeholder={kite?.has_secret ? 'Saved · leave blank to keep' : ''} InputLabelProps={{ shrink: true }}
                         error={!!kiteErrors.api_secret} helperText={kiteErrors.api_secret || 'Stored encrypted, never shown again'} />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={saveKite} disabled={!apiKey.trim() || kiteSaving}>
                {kiteSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </Grid>
          </Grid>
        </Panel>

        <Panel>
          <Box sx={{ mb: 2.5 }}>
            <SectionTitle icon={<ScienceOutlined />} title="Trading mode" subtitle="Switching requires an empty portfolio." />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ModeCard selected={cfg.mode === 'PAPER'} title="Paper (demo)" onClick={() => cfg.mode !== 'PAPER' && switchMode('PAPER')}
                        text="Real Kite prices, pretend fills. No orders reach Zerodha. Run for at least 3 months." />
            </Grid>
            <Grid item xs={12} md={6}>
              <ModeCard live selected={cfg.mode === 'LIVE'} title="Live (real money)"
                        onClick={() => { if (cfg.mode !== 'LIVE') { setConfirmText(''); setLiveDialog(true); } }}
                        text="Real delivery (CNC) orders on your Zerodha account on each rebalance day." />
            </Grid>
          </Grid>
        </Panel>

        <Panel>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2.5 }}>
            <SectionTitle icon={<AutoGraphOutlined />} title="Momentum rules" subtitle="Which stocks to hold and when to re-rank them" />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ color: t.text2 }}>Strategy</Typography>
              <Switch checked={cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} inputProps={{ 'aria-label': 'Strategy enabled' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 28 }}>{cfg.enabled ? 'On' : 'Off'}</Typography>
            </Stack>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>{field('top_n', 'Stocks held', { type: 'number', helperText: `≈ ${inr(perStock)} each` })}</Grid>
            <Grid item xs={6} md={3}>{field('buffer_rank', 'Keep while rank ≤', { type: 'number', helperText: 'Reduces churn and charges' })}</Grid>
            <Grid item xs={6} md={3}>{field('lookback_days', 'Momentum window (days)', { type: 'number', helperText: '252 ≈ 12 months' })}</Grid>
            <Grid item xs={6} md={3}>{field('skip_days', 'Skip latest (days)', { type: 'number', helperText: '21 ≈ 1 month' })}</Grid>
            <Grid item xs={12} md={6}>
              <Toggle checked={cfg.vol_adjusted} onChange={(v) => set('vol_adjusted', v)} label="Volatility adjusted"
                      title="Volatility-adjusted ranking" text="Prefers smooth trends over wild swings (as the NIFTY200 Momentum 30 index does)." />
            </Grid>
            <Grid item xs={12} md={6}>
              <Toggle checked={cfg.market_filter} onChange={(v) => set('market_filter', v)} label="Market filter"
                      title="Market filter" text={`Hold cash while NIFTY 50 is below its ${cfg.sma_days}-day average. Cuts the deepest drawdowns.`} />
            </Grid>
            <Grid item xs={6} md={3}>{field('sma_days', 'Filter average (days)', { type: 'number' })}</Grid>
            <Grid item xs={6} md={3}>{field('rebalance_day', 'Rebalance day of month', { type: 'number', helperText: 'First trading day on/after' })}</Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple freeSolo options={[]} value={cfg.universe}
                onChange={(_, v) => set('universe', (v as string[]).map((s) => s.toUpperCase().trim()))}
                renderTags={(value, getTagProps) =>
                  value.map((opt, i) => (
                    <Chip {...getTagProps({ index: i })} key={opt} label={opt.replace('NSE:', '')} size="small"
                          color={SYMBOL_RE.test(opt) ? 'default' : 'error'} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" label={`Universe (${cfg.universe.length} stocks ranked each month)`} error={!!errors.universe}
                             helperText={errors.universe || 'NIFTY 200 by default. Type a symbol like NSE:RELIANCE and press Enter. Refresh from NSE once or twice a year.'} />
                )}
              />
            </Grid>
          </Grid>
        </Panel>

        <Panel>
          <Box sx={{ mb: 2.5 }}>
            <SectionTitle icon={<ShieldOutlined />} title="Money" subtitle="Capital, costs and how fills are simulated" />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>{field('capital', 'Capital', { ...rupee, helperText: 'Money the strategy manages' })}</Grid>
            <Grid item xs={12} sm={6} md={3}>{field('cash_reserve_pct', 'Cash reserve', { type: 'number', InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> }, helperText: 'Kept uninvested for charges' })}</Grid>
            <Grid item xs={12} sm={6} md={3}>{field('slippage_pct', 'Backtest slippage', { type: 'number', inputProps: { step: 0.01 }, InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> }, helperText: 'Per order, against you' })}</Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth size="small" label="Charges" value={cfg.include_charges ? 'yes' : 'no'} onChange={(e) => set('include_charges', e.target.value === 'yes')}
                         helperText="STT, exchange, GST, stamp, DP">
                <MenuItem value="yes">Deduct estimated charges</MenuItem>
                <MenuItem value="no">Ignore charges</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Panel>
      </Stack>

      {dirty && (
        <Box sx={{
          position: 'fixed', bottom: 16, left: { xs: 16, md: 232 + 32 }, right: 16, zIndex: 20, maxWidth: 1416, mx: 'auto',
          p: 1.5, pl: 2.5, borderRadius: 2.5, bgcolor: '#1E293B', border: '1px solid #334155',
          display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
        }}>
          <Typography sx={{ flex: 1, fontWeight: 600 }}>You have unsaved changes</Typography>
          <Button onClick={load} disabled={saving}>Discard</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? <CircularProgress size={20} color="inherit" /> : 'Save changes'}</Button>
        </Box>
      )}

      <Dialog open={liveDialog} onClose={() => setLiveDialog(false)} aria-labelledby="live-title">
        <DialogTitle id="live-title">Switch to LIVE trading?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>Real delivery orders will be placed on your Zerodha account with real money on each rebalance day.</Alert>
          <DialogContentText sx={{ mb: 1.5 }}>Type <b>LIVE</b> to confirm.</DialogContentText>
          <TextField autoFocus fullWidth size="small" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} inputProps={{ 'aria-label': 'Type LIVE to confirm' }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLiveDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={confirmText !== 'LIVE'} onClick={() => { setLiveDialog(false); switchMode('LIVE'); }}>Go live</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack?.severity ?? 'success'} onClose={() => setSnack(null)} variant="filled">{snack?.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
